import * as Cesium from "cesium";
import { cloneEntity, getUsernameAndUserIdFormat, showToast } from "../Utility";
import Type from "../Types";
import { arcDrawingActive } from "./CreateEntityOnQuadraticBezier";
import { disableCameraControls, restoreCameraControls } from "../MapUtility";

export const handleCuttingGeojsonLines = (viewer, originalCameraControls, targetDataSource, pushUndo, userData, layerForId = null, setMapObjects = null) => {
  const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);

  // ---- Drawing state ----
  let isDrawing = false;
  let drawPoints = [];
  let drawingEntity = null;

  let lastWinPos = null;

  // ========= Constants (planar helpers) =========
  const EPS_M = 0.02;
  const ON_SEG_PAD_M = 0.02;
  const CUT_EXTEND_M = 0.5;
  const DEDUPE_M = 0.05;
  const ANGLE_SIN_MIN = 0.15;

  // ========= Helpers =========

  // build ENU and its inverse at origin
  const makeLocalTransforms = (origin) => {
    if (!origin) return { enu: null, inv: null };
    try {
      const enu = Cesium.Transforms.eastNorthUpToFixedFrame(origin);
      const inv = Cesium.Matrix4.inverse(enu, new Cesium.Matrix4());
      return { enu, inv };
    } catch {
      return { enu: null, inv: null };
    }
  };

  // project world cartesian into local XY using inv transform
  const projectToLocalXY = (p, inv) => {
    if (!inv || !p) return null;
    const v = Cesium.Matrix4.multiplyByPoint(inv, p, new Cesium.Cartesian3());
    return { x: v.x, y: v.y };
  };

  // segment-segment intersection in local XY with parallel guard and padding
  const segSegIntersectXY = (a, b, c, d) => {
    const r = { x: b.x - a.x, y: b.y - a.y };
    const s = { x: d.x - c.x, y: d.y - c.y };
    const rxs = r.x * s.y - r.y * s.x;
    const rLen = Math.hypot(r.x, r.y) || EPS_M;
    const sLen = Math.hypot(s.x, s.y) || EPS_M;

    // require minimum crossing angle
    const sinAngle = Math.abs(rxs) / (rLen * sLen);
    if (sinAngle < ANGLE_SIN_MIN) return null;

    const padT = Math.min(ON_SEG_PAD_M / rLen, 0.05);
    const padU = Math.min(ON_SEG_PAD_M / sLen, 0.05);

    const t = ((c.x - a.x) * s.y - (c.y - a.y) * s.x) / (rxs || EPS_M * EPS_M);
    const u = ((c.x - a.x) * r.y - (c.y - a.y) * r.x) / (rxs || EPS_M * EPS_M);

    if (t >= -padT && t <= 1 + padT && u >= -padU && u <= 1 + padU) {
      return { tOnC: u, pointLocal: { x: a.x + t * r.x, y: a.y + t * r.y } };
    }
    return null;
  };

  // extend segment by length L both directions
  const extendSegmentLocal = (a, b, L) => {
    const vx = b.x - a.x;
    const vy = b.y - a.y;
    const len = Math.hypot(vx, vy) || 1;
    const ux = vx / len;
    const uy = vy / len;
    return [
      { x: a.x - ux * L, y: a.y - uy * L },
      { x: b.x + ux * L, y: b.y + uy * L },
    ];
  };

  // planar intersection using local ENU to avoid lon/lat distortions
  const intersectMetricWithT = (p1, p2, q1, q2) => {
    // midpoint of target segment for stable local frame
    const midTarget = Cesium.Cartesian3.multiplyByScalar(Cesium.Cartesian3.add(q1, q2, new Cesium.Cartesian3()), 0.5, new Cesium.Cartesian3());
    const transforms = makeLocalTransforms(midTarget);
    const inv = transforms.inv;
    const enu = transforms.enu;
    if (!inv || !enu) return null;

    const A = projectToLocalXY(p1, inv);
    const B = projectToLocalXY(p2, inv);
    const C = projectToLocalXY(q1, inv);
    const D = projectToLocalXY(q2, inv);
    if (!A || !B || !C || !D) return null;

    // smart extension based on cut length
    const cutLen = Math.hypot(B.x - A.x, B.y - A.y);
    let ext = CUT_EXTEND_M;
    if (cutLen > 0) ext = Math.min(CUT_EXTEND_M, Math.max(0.1, cutLen * 0.5));
    const [Ae, Be] = extendSegmentLocal(A, B, ext);

    const hit = segSegIntersectXY(Ae, Be, C, D);
    if (!hit) return null;

    // recompute t on original AB
    const r = { x: B.x - A.x, y: B.y - A.y };
    const rLenSq = r.x * r.x + r.y * r.y || EPS_M * EPS_M;
    const tOnAB = ((hit.pointLocal.x - A.x) * r.x + (hit.pointLocal.y - A.y) * r.y) / rLenSq;

    const padTOrig = Math.min(ON_SEG_PAD_M / Math.sqrt(rLenSq), 0.05);
    if (tOnAB < -padTOrig || tOnAB > 1 + padTOrig) return null;

    const tClampedOnAB = Math.max(0, Math.min(1, tOnAB));
    const pointLocalOnOriginal = { x: A.x + tClampedOnAB * r.x, y: A.y + tClampedOnAB * r.y };

    // convert to world using ENU
    const localPoint = new Cesium.Cartesian3(pointLocalOnOriginal.x, pointLocalOnOriginal.y, 0);
    const worldPoint = Cesium.Matrix4.multiplyByPoint(enu, localPoint, new Cesium.Cartesian3());

    // robust u for target segment
    let u = hit.tOnC;
    if (!Number.isFinite(u)) {
      const qdx = D.x - C.x;
      const qdy = D.y - C.y;
      if (Math.abs(qdx) >= Math.abs(qdy)) u = (pointLocalOnOriginal.x - C.x) / (qdx || EPS_M);
      else u = (pointLocalOnOriginal.y - C.y) / (qdy || EPS_M);
    }
    const uClamped = Math.max(0, Math.min(1, u));

    return { point: worldPoint, t: uClamped };
  };

  // planar distance using local ENU at midpoint
  const planarDistance = (a, b) => {
    const mid = Cesium.Cartesian3.multiplyByScalar(Cesium.Cartesian3.add(a, b, new Cesium.Cartesian3()), 0.5, new Cesium.Cartesian3());
    const t = makeLocalTransforms(mid);
    const inv = t.inv;
    if (!inv) return Cesium.Cartesian3.distance(a, b);
    const A = projectToLocalXY(a, inv);
    const B = projectToLocalXY(b, inv);
    if (!A || !B) return Cesium.Cartesian3.distance(a, b);
    return Math.hypot(A.x - B.x, A.y - B.y);
  };

  // distance from point to segment in local XY
  const distPointToSegmentXY = (a, b, p) => {
    const mid = Cesium.Cartesian3.multiplyByScalar(Cesium.Cartesian3.add(a, b, new Cesium.Cartesian3()), 0.5, new Cesium.Cartesian3());
    const t = makeLocalTransforms(mid);
    const inv = t.inv;
    if (!inv) return Infinity;
    const A = projectToLocalXY(a, inv);
    const B = projectToLocalXY(b, inv);
    const P = projectToLocalXY(p, inv);
    if (!A || !B || !P) return Infinity;
    const abx = B.x - A.x,
      aby = B.y - A.y;
    const apx = P.x - A.x,
      apy = P.y - A.y;
    const denom = abx * abx + aby * aby || 1e-12;
    let tparam = (apx * abx + apy * aby) / denom;
    tparam = Math.max(0, Math.min(1, tparam));
    const closest = { x: A.x + tparam * abx, y: A.y + tparam * aby };
    return Math.hypot(P.x - closest.x, P.y - closest.y);
  };

  const pointToPolylineDistance = (p, positions) => {
    let d = Infinity;
    for (let i = 1; i < positions.length; i++) {
      d = Math.min(d, distPointToSegmentXY(positions[i - 1], positions[i], p));
    }
    return d;
  };

  const dedupeIntersections = (arr) => {
    const out = [];
    for (const it of arr) {
      const dup = out.find((o) => planarDistance(o.point, it.point) <= DEDUPE_M);
      if (!dup) out.push(it);
    }
    return out;
  };

  // find intersection points of cutLine with targetPositions
  const findIntersectionPoints = (cutLine, targetPositions) => {
    const out = [];
    for (let i = 0; i < targetPositions.length - 1; i++) {
      const hit = intersectMetricWithT(cutLine[0], cutLine[1], targetPositions[i], targetPositions[i + 1]);
      if (hit) out.push({ index: i + 1, point: hit.point, t: hit.t });
    }
    out.sort((a, b) => a.index - b.index || a.t - b.t);
    return dedupeIntersections(out);
  };

  // split polyline handling multiple intersections per segment and ordering
  const splitPolylineAtIntersections = (polyline, intersections) => {
    if (!intersections?.length) return [polyline];
    const groups = new Map();
    intersections.forEach((it) => {
      if (!groups.has(it.index)) groups.set(it.index, []);
      groups.get(it.index).push(it);
    });
    const segments = [];
    let current = [];
    for (let i = 0; i < polyline.length; i++) {
      current.push(polyline[i]);
      const arr = groups.get(i + 1);
      if (arr?.length) {
        arr.sort((a, b) => a.t - b.t);
        for (const it of arr) {
          const last = current[current.length - 1];
          const same = planarDistance(last, it.point) <= EPS_M;
          if (!same) current.push(it.point);
          segments.push(current);
          current = [it.point];
        }
      }
    }
    if (current.length > 1) segments.push(current);
    return segments;
  };

  const computePolylineLength = (positions) => {
    if (!positions || positions.length < 2) return 0;
    let len = 0;
    for (let i = 0; i < positions.length - 1; i++) len += Cesium.Cartesian3.distance(positions[i], positions[i + 1]);
    return len;
  };

  /** Preserve all props exactly; do not remove 'line-id' or 'lane-id'. */
  const copyProps = (propsObj) => ({ ...(propsObj || {}) });

  const readFeatureId = (entity) => {
    if (!entity?.properties) return undefined;
    const now = Cesium.JulianDate.now();
    const props = entity.properties.getValue?.(now) || {};
    const num = Number(props.id);
    return Number.isFinite(num) ? num : undefined;
  };

  const getMaxPropertyIdInDataSource = (dataSource) => {
    let maxId = -Infinity;
    if (!dataSource?.entities?.values) return -Infinity;
    dataSource.entities.values.forEach((e) => {
      const idNum = readFeatureId(e);
      if (Number.isFinite(idNum) && idNum > maxId) maxId = idNum;
    });
    return maxId;
  };

  /** Compute the global max numeric id across a list of data sources. */
  const getGlobalMaxIdAcross = (dsList) => {
    let max = -Infinity;
    dsList.forEach((ds) => {
      const m = getMaxPropertyIdInDataSource(ds);
      if (Number.isFinite(m) && m > max) max = m;
    });
    return max;
  };

  const collectIdsFromGeoJSON = (gjson, out = new Set()) => {
    if (!gjson?.features || !Array.isArray(gjson.features)) return out;

    for (const f of gjson.features) {
      const n = Number(f?.properties?.id);
      if (Number.isFinite(n)) out.add(n);
    }

    return out;
  };

  const getStoredMaxIdFromLayer = (layer) => {
    const candidates = [layer?.bboxMaxFeaturePropertiesId, layer?.bboxMaxId];

    for (const candidate of candidates) {
      const value = Number(candidate);
      if (Number.isFinite(value)) return value;
    }

    return null;
  };

  const buildUsedFeatureIdSet = (dsList, layer) => {
    const ids = new Set();

    dsList.forEach((ds) => {
      (ds?.entities?.values || []).forEach((e) => {
        const idNum = readFeatureId(e);
        if (Number.isFinite(idNum)) ids.add(idNum);
      });
    });

    for (const gjson of [layer?.geojson, layer?.fullGeojson, layer?.hiddenGeojson, layer?.original_geojson]) {
      collectIdsFromGeoJSON(gjson, ids);
    }

    return ids;
  };

  const commitAllocatedIdToLayer = (layer, nextId) => {
    if (!layer || !Number.isFinite(nextId)) return;

    layer.geojson_last_id = nextId;
    layer.bboxMaxId = nextId;
    layer.bboxMaxFeaturePropertiesId = nextId;

    if (typeof setMapObjects === "function") {
      setMapObjects((prev) =>
        prev.map((o) =>
          String(o?.id) === String(layer.id) || String(o?.layer_id) === String(layer.layer_id)
            ? {
                ...o,
                geojson_last_id: nextId,
                bboxMaxId: nextId,
                bboxMaxFeaturePropertiesId: nextId,
              }
            : o,
        ),
      );
    }
  };

  const getMouseCartesian = (winPos) => {
    if (!winPos) return null;
    let c = viewer.scene.pickPosition(winPos);
    if (c) return c;
    c = viewer.camera.pickEllipsoid(winPos);
    if (c) return c;
    if (viewer.scene.globe) {
      const ray = viewer.camera.getPickRay(winPos);
      if (ray) {
        c = viewer.scene.globe.pick(ray, viewer.scene);
        if (c) return c;
      }
    }
    return null;
  };

  const cleanupDrawingOverlay = () => {
    if (drawingEntity) {
      viewer.entities.remove(drawingEntity);
      drawingEntity = null;
    }
    drawPoints = [];
  };

  const restoreCameraSafe = () => {
    try {
      restoreCameraControls(viewer, originalCameraControls);
    } catch {}
  };

  // ========= Drawing handlers =========

  const startDrawing = (click) => {
    const pos = getMouseCartesian(click.position);
    if (!Cesium.defined(pos)) return;

    isDrawing = true;
    drawPoints = [pos];

    drawingEntity = viewer.entities.add({
      polyline: {
        positions: new Cesium.CallbackProperty(() => drawPoints, false),
        width: 4,
        material: Cesium.Color.YELLOW.withAlpha(0.7),
        clampToGround: true,
      },
    });

    disableCameraControls(viewer, originalCameraControls);
    lastWinPos = click.position;
  };

  const continueDrawing = (movement) => {
    if (!isDrawing) return;
    lastWinPos = movement.endPosition || movement.startPosition || lastWinPos;
    const pos = getMouseCartesian(lastWinPos);
    if (Cesium.defined(pos)) drawPoints[1] = pos;
  };

  const finalizeCutWithWindowPos = (winPos) => {
    restoreCameraSafe();

    if (!isDrawing) {
      cleanupDrawingOverlay();
      return;
    }
    isDrawing = false;

    const endPos = getMouseCartesian(winPos);
    if (!Cesium.defined(endPos)) {
      cleanupDrawingOverlay();
      return;
    }

    drawPoints[1] = endPos;
    const drawnLine = [drawPoints[0], drawPoints[1]];

    // Determine which data sources to inspect (target only, or all)
    const dsList = targetDataSource ? [targetDataSource] : viewer.dataSources?._dataSources || [];

    // Precompute a GLOBAL max id across all DS to guarantee uniqueness across multiple splits
    let globalMaxId = getGlobalMaxIdAcross(dsList);
    const storedLayerMaxId = getStoredMaxIdFromLayer(layerForId);
    if (Number.isFinite(storedLayerMaxId) && storedLayerMaxId > globalMaxId) globalMaxId = storedLayerMaxId;
    const usedFeatureIds = buildUsedFeatureIdSet(dsList, layerForId);

    // Collect eligible entities to cut
    const allEntities = [];
    dsList.forEach((ds) => {
      (ds.entities?.values || []).forEach((e) => {
        if (e.polyline && e.properties && Object.values(Type.ASSET).includes(e.geojsonType)) {
          allEntities.push({ ds, e });
        }
      });
    });

    const globalUndoSnapshot = { type: "multi-split", splits: [] };

    allEntities.forEach(({ ds, e: entity }) => {
      const now = Cesium.JulianDate.now();
      const polylinePositions = entity.polyline.positions.getValue(now);
      if (!polylinePositions || polylinePositions.length < 2) return;

      const intersections = findIntersectionPoints(drawnLine, polylinePositions);
      if (intersections.length === 0) return;

      // --- Enforce: must have a valid numeric id; otherwise skip and toast in English
      const rawProps = entity.properties?.getValue?.(now) || {};
      const hasIdKey = Object.prototype.hasOwnProperty.call(rawProps, "id");
      const originalFeatureId = hasIdKey ? Number(rawProps.id) : NaN;

      if (!hasIdKey || !Number.isFinite(originalFeatureId)) {
        showToast("No valid 'id' on the line. Cutting skipped.", Type.NOTIFICATION.INFO);
        return;
      }

      const segments = splitPolylineAtIntersections(polylinePositions, intersections);
      if (!segments || segments.length < 2) return;

      const originalProps = copyProps(rawProps);

      // Choose the longest segment to retain the original id
      const lengths = segments.map((seg) => computePolylineLength(seg));
      let longestIdx = 0;
      for (let i = 1; i < lengths.length; i++) if (lengths[i] > lengths[longestIdx]) longestIdx = i;

      // Make sure the allocator starts above the current GLOBAL maximum (including this entity's id)
      globalMaxId = Number.isFinite(globalMaxId) ? Math.max(globalMaxId, originalFeatureId) : originalFeatureId;

      const undoSnapshot = { type: "split", originalEntity: cloneEntity(entity), newEntities: [] };

      // Remove the original and add segments back as new entities
      ds.entities.remove(entity);

      segments.forEach((segmentPositions, i) => {
        const newProps = { ...originalProps };
        newProps.modifier = getUsernameAndUserIdFormat(userData);

        if (i === longestIdx) {
          // Keep the original id for the longest piece
          newProps.id = originalFeatureId;
        } else {
          // Allocate a fresh unique id: always larger than any existing (GLOBAL), step +2
          let nextId = (Number.isFinite(globalMaxId) ? globalMaxId : 0) + 2;
          while (usedFeatureIds.has(nextId)) nextId += 2;
          usedFeatureIds.add(nextId);
          newProps.id = nextId;
          globalMaxId = nextId;
          commitAllocatedIdToLayer(layerForId, nextId);
        }

        const newEntity = new Cesium.Entity({
          polyline: {
            positions: segmentPositions,
            width: entity.polyline.width,
            material: entity.polyline.material,
            clampToGround: true,
            heightReference: entity.polyline.heightReference,
          },
          geojsonType: entity.geojsonType,
          properties: new Cesium.PropertyBag(newProps),
          id: `${entity.id}_split_${i}`,
        });

        ds.entities.add(newEntity);
        undoSnapshot.newEntities.push(newEntity.id);
      });

      globalUndoSnapshot.splits.push(undoSnapshot);
    });

    if (globalUndoSnapshot.splits.length > 0) {
      if (!arcDrawingActive) {
        pushUndo(globalUndoSnapshot);
      }

      showToast("The selected line is sliced", Type.NOTIFICATION.INFO);
    }

    cleanupDrawingOverlay();
  };

  const endDrawing = (click) => {
    finalizeCutWithWindowPos(click.position);
  };

  handler.setInputAction(() => {
    if (!isDrawing) return;
    restoreCameraSafe();
    isDrawing = false;
    cleanupDrawingOverlay();
  }, Cesium.ScreenSpaceEventType.RIGHT_DOWN);

  const onKeyDown = (ev) => {
    if (ev.key === "Escape" && isDrawing) {
      restoreCameraSafe();
      isDrawing = false;
      cleanupDrawingOverlay();
    }
  };

  const onWinPointerUp = () => {
    if (!isDrawing) return;
    const pos = lastWinPos instanceof Cesium.Cartesian2 ? lastWinPos : null;
    finalizeCutWithWindowPos(pos);
  };

  const onWinBlurOrHide = () => {
    if (!isDrawing) return;
    restoreCameraSafe();
    isDrawing = false;
    cleanupDrawingOverlay();
  };

  window.addEventListener("keydown", onKeyDown, true);
  window.addEventListener("pointerup", onWinPointerUp, true);
  window.addEventListener("mouseup", onWinPointerUp, true);
  window.addEventListener("blur", onWinBlurOrHide, true);
  document.addEventListener("visibilitychange", onWinBlurOrHide, true);

  handler.setInputAction(startDrawing, Cesium.ScreenSpaceEventType.LEFT_DOWN);
  handler.setInputAction(continueDrawing, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
  handler.setInputAction(endDrawing, Cesium.ScreenSpaceEventType.LEFT_UP);

  const _destroy = handler.destroy.bind(handler);
  handler.destroy = () => {
    try {
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("pointerup", onWinPointerUp, true);
      window.removeEventListener("mouseup", onWinPointerUp, true);
      window.removeEventListener("blur", onWinBlurOrHide, true);
      document.removeEventListener("visibilitychange", onWinBlurOrHide, true);

      try {
        handler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOWN);
        handler.removeInputAction(Cesium.ScreenSpaceEventType.MOUSE_MOVE);
        handler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_UP);
        handler.removeInputAction(Cesium.ScreenSpaceEventType.RIGHT_DOWN);
      } catch {}

      restoreCameraSafe();
      isDrawing = false;

      cleanupDrawingOverlay();
    } finally {
      _destroy();
    }
  };

  return handler;
};
